/**
 * lib/badges/popularity.ts — materializer for `products.popularity_score`
 * (+ `popularity_velocity`, `popularity_computed_at`).
 *
 * WHAT IT DOES (once activated): recomputes each product's popularity from the
 * REAL `clicks` table — trailing-30-day non-bot outbound affiliate clicks — and
 * writes it onto the product row so the badge resolver can fire `best_seller`
 * (score >= site cutoff) and `trending` (7d-vs-prior-7d velocity) server-side
 * without a per-card PostHog fetch (spec §3.3). No PostHog dependency: `clicks`
 * is a first-party, product-keyed, bot-filtered signal (239K+ rows, refreshed
 * continuously by /r/ redirect logging).
 *
 * HONEST BY CONSTRUCTION:
 *   - Products with zero clicks in the window decay to score 0 (never left stale).
 *   - Velocity is NULL when there is no prior-window baseline (an infinite ratio
 *     is not a real number — we do not fake one, so a brand-new spike simply does
 *     not earn `trending` until it has a comparable prior week).
 *   - `products.updated_at` is deliberately NOT touched (verified 2026-07-18: no
 *     updated_at auto-bump trigger on products) — a popularity refresh must never
 *     emit a `<lastmod>` freshness signal. The write also skips rows whose score
 *     is unchanged, minimizing branding/advertiser-stat trigger fires.
 *
 * ACTIVATION STATE (07/21/2026): the migration
 * (supabase/migrations/20260718110000_badge_popularity_score_item64.sql) is
 * APPLIED live (columns + `refresh_product_popularity_scores` exist; 5,169
 * products scored) and the cron route IS scheduled in vercel.json
 * (0 5 * * *, owner approval). The one remaining gate is the env flag:
 * `BADGE_POPULARITY_CRON_ENABLED === 'true'` — until it is set in the Vercel
 * env, every cron invocation is a healthy { status: 'dormant' } no-op.
 * See docs/Operations.md for the activation record.
 */

import { opsSupabase } from '@/lib/routines/supabase/client'

/** Trailing window (days) the popularity score counts clicks over. */
export const POPULARITY_WINDOW_DAYS = 30
/** Velocity compares the last `TRENDING_RECENT_DAYS` to the prior equal window. */
export const TRENDING_RECENT_DAYS = 7

/** Env flag that must equal 'true' for the materializer to write anything. */
export const POPULARITY_CRON_ENABLED_ENV = 'BADGE_POPULARITY_CRON_ENABLED'

export interface MaterializeOptions {
  /** Compute the would-change count only; write nothing. */
  dryRun?: boolean
  /** Override the trailing score window (days). */
  windowDays?: number
  /** Override the velocity half-window (days). */
  recentDays?: number
  /**
   * Ignore the env kill-switch. Only the CLI/dry-run harness sets this; the cron
   * route always honors the env gate. A dry run is read-only regardless.
   */
  force?: boolean
}

export type MaterializeResult =
  | { status: 'dormant'; reason: string }
  | { status: 'ok'; dryRun: boolean; rowsChanged: number; windowDays: number; recentDays: number }
  | { status: 'error'; error: string }

/**
 * Recompute + persist popularity via the `refresh_product_popularity_scores`
 * Postgres function (defined in the staged migration). All the heavy lifting is
 * one set-based statement server-side; this wrapper only enforces the env gate,
 * threads options, and normalizes the result. Fails closed on any error.
 */
export async function materializePopularityScores(
  opts: MaterializeOptions = {}
): Promise<MaterializeResult> {
  const dryRun = opts.dryRun === true
  const windowDays = opts.windowDays ?? POPULARITY_WINDOW_DAYS
  const recentDays = opts.recentDays ?? TRENDING_RECENT_DAYS

  const enabled = process.env[POPULARITY_CRON_ENABLED_ENV] === 'true'
  if (!enabled && !opts.force) {
    return {
      status: 'dormant',
      reason: `${POPULARITY_CRON_ENABLED_ENV} !== 'true' — badge popularity materializer is dormant`,
    }
  }

  try {
    const supabase = opsSupabase()
    const { data, error } = await supabase.rpc('refresh_product_popularity_scores', {
      window_days: windowDays,
      recent_days: recentDays,
      dry_run: dryRun,
    })
    if (error) {
      return { status: 'error', error: error.message }
    }
    // The function returns a single row { rows_updated bigint }.
    const rowsChanged = normalizeCount(data)
    return { status: 'ok', dryRun, rowsChanged, windowDays, recentDays }
  } catch (err) {
    return { status: 'error', error: err instanceof Error ? err.message : String(err) }
  }
}

function normalizeCount(data: unknown): number {
  if (Array.isArray(data)) {
    const row = data[0] as { rows_updated?: number | string } | undefined
    return Number(row?.rows_updated ?? 0) || 0
  }
  if (data && typeof data === 'object') {
    const row = data as { rows_updated?: number | string }
    return Number(row.rows_updated ?? 0) || 0
  }
  return Number(data ?? 0) || 0
}

/**
 * Compute a best-seller cutoff from the live score distribution — the `best_seller`
 * badge fires for scores at/above the `percentile` (spec §3.3: "score above the
 * Nth percentile"). Pure so the API can call it on the batch it just loaded, or a
 * job can precompute a site-wide value. Returns null when there is no positive
 * signal to threshold against (→ resolver falls back to its constant floor).
 *
 * @param scores    popularity_score values (nulls / non-positives ignored)
 * @param percentile 0..1 (default 0.9 = top decile)
 */
export function bestSellerThresholdFromScores(
  scores: Array<number | null | undefined>,
  percentile = 0.9
): number | null {
  const positive = scores
    .filter((s): s is number => typeof s === 'number' && Number.isFinite(s) && s > 0)
    .sort((a, b) => a - b)
  if (positive.length === 0) return null
  const p = Math.min(Math.max(percentile, 0), 1)
  const idx = Math.min(positive.length - 1, Math.floor(p * (positive.length - 1)))
  return positive[idx]
}
